import * as React from "react";
import { css } from "@emotion/core";
import { Button, Card, CardList } from "@blueprintjs/core";
import { formatBytes } from "utils";
import { ProcessedImage } from "components/Orders/state/types";
import { ImagesMachineContext } from "components/Orders/state/imagesMachine";
import { OrderMachineContext } from "components/Orders/state/orderMachine";
import { formatCaptureTimeForDisplay } from "components/Orders/utils/validation";

function ImagePreview({ image }: { image: ProcessedImage }) {
  const imagesMachine = ImagesMachineContext.useActorRef();

  const idle = ImagesMachineContext.useSelector((state) =>
    state.matches("idle")
  );

  const timezone = OrderMachineContext.useSelector((s) => s.context.timezone);

  const captured = formatCaptureTimeForDisplay(image, timezone);

  const [preview, setPreview] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (image.file instanceof File) {
      const preview = URL.createObjectURL(image.file);
      setPreview(preview);

      return () => {
        if (preview) {
          URL.revokeObjectURL(preview);
          setPreview(null);
        }
      };
    }
  }, [image.file]);

  return (
    <div>
      <CardList>
        <Card
          css={css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
          `}
        >
          <span>{image.file.name}</span>
          <Button
            icon="cross"
            onClick={() =>
              imagesMachine.send({ type: "MARKER_DESELECT", id: image.id })
            }
          />
        </Card>
        <Card>
          <div
            css={css`
              padding: 1rem 0;
            `}
          >
            <img
              src={preview}
              css={css`
                width: 100%;
                border: 4px solid var(--neutral-1);
              `}
            />
          </div>
        </Card>
        <Card>
          <div
            css={css`
              width: 100%;
              display: flex;
              flex-direction: row;
              justify-content: space-between;
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
              `}
            >
              <span>{captured.date} </span>
              <span>{captured.time}</span>
            </div>
            <span>{formatBytes(image.file.size)}</span>
          </div>
        </Card>
        <Card
          css={css`
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
          `}
        >
          <Button
            icon="trash"
            intent="danger"
            css={css`
              width: 100%;
            `}
            disabled={idle === false}
            onClick={() => {
              imagesMachine.send({ type: "REMOVE_FILE", id: image.id });
            }}
          >
            Remove Image
          </Button>
        </Card>
      </CardList>
    </div>
  );
}

interface ImagePreviewsProps {
  imageUploads: ProcessedImage[];
  viewing: string[];
}

function ImagePreviews(props: ImagePreviewsProps) {
  return (
    <div
      css={css`
        height: 100%;
        display: flex;
        flex-direction: column;
        padding: 1rem;
        gap: 1rem;
        overflow-y: auto;
        background-color: var(--neutral-7);
        border: 1px solid var(--neutral-5);
      `}
    >
      {props.imageUploads
        .filter((image) => props.viewing.includes(image.id))
        .map((image) => (
          <ImagePreview key={image.id} image={image} />
        ))}
    </div>
  );
}

interface ImagePreviewPanelProps {
  imageUploads: ProcessedImage[];
}

export default function ImagePreviewPanel(props: ImagePreviewPanelProps) {
  const viewing = ImagesMachineContext.useSelector((state) =>
    state.context.selected.length > 0 ? state.context.selected : null
  );

  if (!viewing) {
    return null;
  }

  return <ImagePreviews imageUploads={props.imageUploads} viewing={viewing} />;
}
